import { clsx } from 'clsx';
import s from './ui.module.css';

/** Outer and inner radius of the star in a 24-unit box, and its centre (a little low, as stars sit). */
const OUTER = 11.5;
const INNER = 4.7;
const CX = 12;
const CY = 12.8;
/** Where the light comes from: the top left. */
const LIGHT = { x: -0.62, y: -0.78 };

type Tone = 1 | 2 | 3 | 4;

const at = (deg: number, r: number) => {
  const a = (deg * Math.PI) / 180;
  return { x: CX + Math.cos(a) * r, y: CY + Math.sin(a) * r };
};
const pt = (p: { x: number; y: number }) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`;

/**
 * Ten facets, two per arm, each a triangle from the centre to the arm's tip. A facet faces sideways from its
 * arm's ridge and a little along it; the closer it faces the light, the lighter its tone.
 */
export const LEAD_STAR_FACETS: readonly { points: string; tone: Tone }[] = Array.from(
  { length: 5 },
  (_, k) => {
    const tip = -90 + 72 * k;
    return ([-1, 1] as const).map((side) => {
      const normal = ((tip + side * 90) * Math.PI) / 180;
      const along = (tip * Math.PI) / 180;
      const nx = 0.85 * Math.cos(normal) + 0.35 * Math.cos(along);
      const ny = 0.85 * Math.sin(normal) + 0.35 * Math.sin(along);
      const lit = (nx * LIGHT.x + ny * LIGHT.y) / Math.hypot(nx, ny);
      const tone: Tone = lit > 0.8 ? 1 : lit > 0.5 ? 2 : lit > -0.5 ? 3 : 4;
      const notch = at(tip + side * 36, INNER);
      return { points: [pt({ x: CX, y: CY }), pt(at(tip, OUTER)), pt(notch)].join(' '), tone };
    });
  },
).flat();

/**
 * The bevelled gold star of someone leading an area at L5 (ADR 0018). Decorative: the text next to it says
 * what it counts.
 */
export function LeadStar({ size = 14, className }: { size?: number; className?: string }) {
  return (
    <svg
      className={clsx(s.leadStar, className)}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      {LEAD_STAR_FACETS.map((f) => (
        <polygon key={f.points} points={f.points} data-tone={f.tone} />
      ))}
    </svg>
  );
}
